<template>
  <v-container class="pa-0">
    <cancel-load-dialog  v-model="showCancelModal"
                         @refresh="$emit('reload', $event)"
                         :listing-human-id="_.get(planningLoad, 'humanId', null)"
                         :booked-load-id="cancelBookedLoadId"
                         :load-id="cancelBookedHumanId">
    </cancel-load-dialog>

    <v-layout column>
      <v-flex>
        <v-card outline :flat="dispatchedLoads">
          <v-toolbar fab flat dense v-if="!dispatchedLoads">

            <v-toolbar-title>
              Booked Loads ({{ visibleBookedLoads.length }})
            </v-toolbar-title>
            <v-spacer/>
            <v-btn-toggle multiple>
              <v-btn v-model="showCanceledBookedLoads">
                <v-icon :color="showCanceledBookedLoads ? 'accent' : null">
                  highlight_off
                </v-icon>
              </v-btn>
              <v-btn v-model="groupBookedLoadsByCarrier">
                <v-icon :color="groupBookedLoadsByCarrier ? 'accent' : null">
                  local_shipping
                </v-icon>
              </v-btn>
            </v-btn-toggle>
          </v-toolbar>
          <v-responsive v-if="loading">
            <v-layout column justify-center align-center fill-height class="py-3">
              <v-progress-circular indeterminate
                                   color="primary"
                                   class="mx-auto">
              </v-progress-circular>
            </v-layout>
          </v-responsive>
          <template v-else>
            <v-list three-line v-if="groupBookedLoadsByCarrier">
              <template v-if="!bookedLoadsByCarrier.length">
                <v-list-tile>
                  <v-list-tile-action>
                    <v-icon color="accent">
                      offline_bolt
                    </v-icon>
                  </v-list-tile-action>
                  <v-list-tile-title>None yet.</v-list-tile-title>
                </v-list-tile>
              </template>
              <template v-for="carrierLoads in bookedLoadsByCarrier"
                        v-else>
                <v-list-group :key="carrierLoads.id">
                  <v-list-tile slot="activator">
                    <v-list-tile-action>
                      <v-chip color="accent" text-color="white" small>
                        {{ carrierLoads.loads.length }}
                      </v-chip>
                    </v-list-tile-action>
                    <v-list-tile-title>{{ carrierLoads.name }}</v-list-tile-title>
                  </v-list-tile>
                  <template v-for="load in sortByPickupTimeAndHumanId(carrierLoads.loads)">
                    <v-list-tile
                      :key="load.humanId"
                      :class="loadStyleClass(load)"
                      ripple
                      avatar>
                      <v-list-tile-action>
                        <v-btn color="accent" fab small @click="viewBookedLoadDetail(load.humanId)">
                          <v-icon>visibility</v-icon>
                        </v-btn>
                      </v-list-tile-action>

                      <v-list-tile-content>
                        <v-list-tile-title class="text--primary">
                          {{ load.tacticalInfo.scheduledPickupTime | formatDate }}
                        </v-list-tile-title>
                        <v-list-tile-sub-title>
                          {{ load.humanId }}
                          <span class="red--text" v-if="load.terms.freightSettlement">
                          , ${{ load.terms.freightSettlement[0].amount }}
                        </span>
                          <span class="green--text" v-if="load.terms.customerSettlement">
                          , ${{ load.terms.customerSettlement.amount }}
                        </span>
                        </v-list-tile-sub-title>
                        <v-list-tile-sub-title>
                        <span class="font-italic" v-if="driverId(load)">
                           {{ driverName(load) }}
                        </span>
                          <span class="font-italic" v-else>(no driver assigned)</span>
                        </v-list-tile-sub-title>
                      </v-list-tile-content>

                      <v-list-tile-avatar>
                        <v-btn fab flat color="accent"
                               :disabled="isBookedLoadCanceled(load)"
                               @click="cancelLoad(load)">
                          <v-icon>cancel</v-icon>
                        </v-btn>
                      </v-list-tile-avatar>
                    </v-list-tile>
                    <v-list-tile v-if="isMyLoad(load) &&
                     !isBookedLoadCanceled(load) && drivers.length">
                      <v-list-tile-action></v-list-tile-action>
                      <v-select-load-driver @refresh="updateBookedLoad($event)"
                                            :load="load"
                                            :company="company">
                      </v-select-load-driver>
                      <template v-if="!hasOfficePermission(loadModel.carrierCompany(load).id) &&
                       loadModel.assignedTruckingCompanyDriverName(load)">
                        <v-list-tile-content>
                          <v-list-tile-title>
                            {{loadModel.assignedTruckingCompanyDriverName(load)}}
                          </v-list-tile-title>
                        </v-list-tile-content>
                      </template>
                    </v-list-tile>
                    <v-divider/>
                  </template>
                </v-list-group>
              </template>
            </v-list>
            <v-list three-line v-else>
              <template v-if="!visibleBookedLoads.length">
                <v-list-tile>
                  <v-list-tile-action>
                    <v-icon color="accent">
                      offline_bolt
                    </v-icon>
                  </v-list-tile-action>
                  <v-list-tile-title>None yet.</v-list-tile-title>
                </v-list-tile>
              </template>
              <template v-else
                        v-for="load in visibleBookedLoads">
                <v-list-group v-if="isMyLoad(load) && !isBookedLoadCanceled(load) && drivers.length"
                              :class="loadStyleClass(load)"
                              :key="`booked_load_${load.humanId}`">
                  <template slot="activator">
                    <v-booked-load-planner-list-tile
                      @cancelLoad="cancelLoad($event)"
                      :is-load-owner="isLoadOwner(load)"
                      :company="company"
                      :load="load"
                      @viewLoad="viewBookedLoadDetail(load.humanId)">
                    </v-booked-load-planner-list-tile>
                  </template>

                  <v-list-tile v-if="isMyLoad(load) && drivers.length">
                    <v-list-tile-action></v-list-tile-action>
                    <v-select-load-driver @refresh="updateBookedLoad($event)"
                                          :load="load"
                                          :company="company">
                    </v-select-load-driver>
                    <template v-if="!hasOfficePermission(loadModel.carrierCompany(load).id) &&
                    loadModel.assignedTruckingCompanyDriverName(load)">
                      <v-list-tile-content>
                        <v-list-tile-title>
                          {{loadModel.assignedTruckingCompanyDriverName(load)}}
                        </v-list-tile-title>
                      </v-list-tile-content>
                    </template>

                  </v-list-tile>
                </v-list-group>
                <v-booked-load-planner-list-tile
                  v-else
                  :is-load-owner="isLoadOwner(load)"
                  @cancelLoad="cancelLoad($event)"
                  :company="company"
                  :key="`load_assign_${load.humanId}`"
                  :load="load"
                  @viewLoad="viewBookedLoadDetail(load.humanId)">
                </v-booked-load-planner-list-tile>

                <v-divider :key="`divider_${load.humanId}`"/>
              </template>
            </v-list>
          </template>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';
import Model from '@/types/models/';
import * as Loads from '@/types/models/load';
import * as Checks from '@/store/permissions';
import bookedLoadMixin from '@/mixins/bookedLoadMixin';
import { driversWithCDN } from '@/types/models/company';
import VSelectLoadDriver from '@/components/SelectLoadDriver';
import CancelLoadDialog from '@/components/modals/CancelLoadDialog';

import VBookedLoadPlannerListTile from './BookedLoadPlannerListTile';

export default {
  name: 'VBooked',
  props: {
    planningLoad: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
    company: {
      type: Object,
    },
    coveredLoads: {
      type: Array,
      default: null,
    },
  },
  components: {
    VSelectLoadDriver,
    VBookedLoadPlannerListTile,
    CancelLoadDialog,
  },
  mixins: [
    bookedLoadMixin,
  ],
  data: () => ({
    showCanceledBookedLoads: true,
    groupBookedLoadsByCarrier: false,
    showCancelModal: false,
    cancelBookedLoadId: null,
    cancelBookedHumanId: null,
  }),
  computed: {
    _() {
      return _;
    },
    ...mapGetters('profiles', {
      isLtnAdmin: 'isLtnAdmin',
    }),
    drivers() {
      return driversWithCDN(this.company);
    },
    visibleBookedLoads() {
      const loads = this.coveredLoads || _.get(this.planningLoad, 'bookedLoads', []);
      const things = _.filter(loads,
        bl => this.showCanceledBookedLoads || !this.isBookedLoadCanceled(bl));

      return this.sortByPickupTimeAndHumanId(things);
    },
    bookedLoadsByCarrier() {
      const loadsByCarrier = {};
      _.reduce(this.visibleBookedLoads, (result, bl) => {
        const hauler = this.bookedLoadHaulingCompany(bl);
        const vendor = this.bookedLoadVendorCarrierCompany(bl);

        const groupId = vendor ? 'vendor-' + hauler.id : 'ltn-' + hauler.id;
        // eslint-disable-next-line no-param-reassign
        (result[groupId] || (result[groupId] = { name: hauler.name, id: groupId, loads: [] })).loads.push(bl);
        return result;
      }, loadsByCarrier);

      return _.sortBy(Object.values(loadsByCarrier), ['name'], ['asc']);
    },
    loadModel() {
      return Model.load;
    },
    dispatchedLoads() {
      return this.coveredLoads != null;
    },
  },
  methods: {
    ...mapGetters('profiles', {
      findCompanyAccount: 'findCompanyAccount',
    }),
    loadStyleClass(load) {
      if (this.isVendorAssigned(load)) {
        return 'vendor_assigned';
      }

      if (!this.isMyLoad(load)) {
        return 'not_mine';
      }

      return this.driverId(load) ? 'assigned' : 'unassigned';
    },
    sortByPickupTimeAndHumanId(loads) {
      return _.sortBy(loads, ['tacticalInfo.scheduledPickupTime', 'humanId'], ['asc', 'asc']);
    },
    canceledBookedTruckCount(load) {
      const booked = _.get(load, 'bookedLoads', []);
      return _.filter(booked, b => this.isBookedLoadCanceled(b)).length;
    },
    activeBookedTruckCount(load) {
      const booked = _.get(load, 'bookedLoads', []);
      return _.filter(booked, b => !this.isBookedLoadCanceled(b)).length;
    },
    viewBookedLoadDetail(bookedLoadId) {
      this.$router.push({ name: 'load.detail', params: { id: bookedLoadId } });
    },
    hasOfficePermission(companyId) {
      const companyAccount = this.findCompanyAccount()(companyId);
      return this.isLtnAdmin || Checks.accountHasOfficePermission(companyAccount);
    },
    driverId(load) {
      return Loads.assignedTruckingCompanyDriverUserId(load);
    },
    driverName(load) {
      return Loads.assignedTruckingCompanyDriverName(load);
    },
    isMyLoad(load) {
      const carrier = this.bookedLoadCarrierCompany(load);
      return carrier && this.company.id === carrier.id;
    },
    isLoadOwner(load) {
      return this.company.id === this.loadModel.listingCompany(load).id;
    },
    isVendorAssigned(load) {
      return this.isBookedLoadVendorAssigned(load);
    },
    cancelLoad(bookedLoad) {
      this.cancelBookedLoadId = bookedLoad.id;
      this.cancelBookedHumanId = bookedLoad.humanId;
      this.showCancelModal = true;
    },
    updateBookedLoad(load) {
      this.$emit('refreshLoad');
      this.$store.dispatch('bookedLoads/updateLoad', load);
    },
  },
};
</script>

<style>
  .unassigned {
    border-left: 3px red solid;
  }

  .assigned {
    border-left: 3px #428BCA solid;
  }

  .vendor_assigned {
    border-left: 3px #2e9e1c solid;
  }

  .not_mine {
    border-left: 3px yellow solid;
  }

</style>



// WEBPACK FOOTER //
// src/pages/Companies/Loads/components/Booked.vue